import React, { useEffect, useMemo, useState } from "react";
import { CalendarCheck, FileText, Paperclip, CheckCircle2, XCircle, RefreshCw, Filter } from "lucide-react";
import api from "@/lib/api";
import { Card, PageHeader, Button, Field, Input, Select, Badge, Modal } from "@/components/ui-kit";
import { toast } from "sonner";

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
};

const STATUS_TONE = { pending: "amber", approved: "emerald", rejected: "red", cancelled: "stone" };
const STATUS_LABEL = { pending: "Menunggu", approved: "Disetujui", rejected: "Ditolak", cancelled: "Dibatalkan" };

/** Iter v34e (#1) — Admin/HR approval page for leave requests. */
export default function CutiAdmin() {
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "pending", category: "", year: new Date().getFullYear() });
  const [reviewModal, setReviewModal] = useState({ open: false, action: null, leave: null, note: "" });

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filter.status) p.set("status", filter.status);
      if (filter.category) p.set("category", filter.category);
      if (filter.year) p.set("year", filter.year);
      const r = await api.get(`/leave-requests?${p.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar cuti");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const openReview = (action, leave) => setReviewModal({ open: true, action, leave, note: "" });

  const submitReview = async () => {
    const { action, leave, note } = reviewModal;
    try {
      await api.post(`/leave-requests/${leave.id}/${action}`, { note });
      toast.success(action === "approve" ? "Disetujui" : "Ditolak");
      setReviewModal({ open: false, action: null, leave: null, note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    }
  };

  return (
    <div className="space-y-6" data-testid="cuti-admin-page">
      <PageHeader overline="HR" title="Persetujuan Cuti & Sakit"
        description="Tinjau & setujui/tolak pengajuan cuti, sakit, dan izin karyawan. Approval otomatis mengurangi kuota karyawan." />

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cuti-admin-summary">
        {[
          { k: "pending", tone: "amber", label: "Menunggu" },
          { k: "approved", tone: "emerald", label: "Disetujui" },
          { k: "rejected", tone: "red", label: "Ditolak" },
          { k: "cancelled", tone: "stone", label: "Dibatalkan" },
        ].map((s) => (
          <Card key={s.k} className={`p-4 border-l-4 border-${s.tone}-400`}>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">{s.label}</div>
            <div className="text-2xl font-display font-bold text-stone-900">{data.summary?.[s.k] || 0}</div>
          </Card>
        ))}
      </div>

      {/* Filter */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <Field label="Status">
            <Select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="cuti-admin-filter-status">
              <option value="pending">Menunggu</option>
              <option value="approved">Disetujui</option>
              <option value="rejected">Ditolak</option>
              <option value="cancelled">Dibatalkan</option>
              <option value="">Semua Status</option>
            </Select>
          </Field>
          <Field label="Kategori">
            <Select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })}>
              <option value="">Semua</option>
              <option value="cuti_tahunan">Cuti Tahunan</option>
              <option value="cuti_khusus">Cuti Khusus</option>
              <option value="sakit">Sakit</option>
              <option value="izin">Izin</option>
            </Select>
          </Field>
          <Field label="Tahun">
            <Input type="number" value={filter.year} onChange={(e) => setFilter({ ...filter, year: Number(e.target.value) || new Date().getFullYear() })} />
          </Field>
          <Button variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
        </div>
      </Card>

      {/* List */}
      <Card className="p-0 overflow-x-auto" data-testid="cuti-admin-list">
        <table className="w-full text-xs">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-600">
            <tr>
              <th className="text-left px-3 py-2.5">Diajukan</th>
              <th className="text-left px-3 py-2.5">Karyawan</th>
              <th className="text-left px-3 py-2.5">Departemen</th>
              <th className="text-left px-3 py-2.5">Jenis</th>
              <th className="text-left px-3 py-2.5">Periode</th>
              <th className="text-right px-3 py-2.5">Hari</th>
              <th className="text-left px-3 py-2.5">Alasan</th>
              <th className="text-center px-3 py-2.5">Status</th>
              <th className="text-center px-3 py-2.5">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {loading && <tr><td colSpan={9} className="px-3 py-12 text-center text-stone-400">Memuat...</td></tr>}
            {!loading && data.rows.length === 0 && <tr><td colSpan={9} className="px-3 py-12 text-center text-stone-400">Tidak ada pengajuan untuk filter ini.</td></tr>}
            {data.rows.map((r) => (
              <tr key={r.id} className="hover:bg-stone-50/40" data-testid={`cuti-admin-row-${r.id}`}>
                <td className="px-3 py-2.5 font-mono text-[11px]">{fmtDate(r.submitted_at)}</td>
                <td className="px-3 py-2.5">
                  <div className="font-medium">{r.employee_name}</div>
                  <div className="text-[10px] text-stone-500 font-mono">{r.employee_code}</div>
                </td>
                <td className="px-3 py-2.5 text-stone-700">{r.department_category || "—"}</td>
                <td className="px-3 py-2.5">{r.category_label}</td>
                <td className="px-3 py-2.5">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</td>
                <td className="px-3 py-2.5 text-right font-mono">{r.days_count}</td>
                <td className="px-3 py-2.5 max-w-[240px] truncate" title={r.reason}>
                  {r.reason}
                  {r.has_attachment && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/leave-requests/${r.id}/attachment`} target="_blank" rel="noreferrer" className="ml-1 text-terra-700" data-testid={`cuti-admin-attach-${r.id}`}>
                      <Paperclip className="h-3 w-3 inline" />
                    </a>
                  )}
                  {r.review_note && r.status !== "pending" && (
                    <div className="text-[10px] text-stone-500 italic mt-0.5">Note: {r.review_note}</div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-center"><Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge></td>
                <td className="px-3 py-2.5 text-center whitespace-nowrap">
                  {r.status === "pending" ? (
                    <div className="flex gap-1 justify-center">
                      <button onClick={() => openReview("approve", r)} className="px-2 py-1 text-[11px] bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1" data-testid={`cuti-approve-${r.id}`}>
                        <CheckCircle2 className="h-3 w-3" /> Setujui
                      </button>
                      <button onClick={() => openReview("reject", r)} className="px-2 py-1 text-[11px] bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1" data-testid={`cuti-reject-${r.id}`}>
                        <XCircle className="h-3 w-3" /> Tolak
                      </button>
                    </div>
                  ) : <span className="text-stone-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Review modal */}
      <Modal open={reviewModal.open} onClose={() => setReviewModal({ open: false, action: null, leave: null, note: "" })}
        title={reviewModal.action === "approve" ? "Setujui Pengajuan" : "Tolak Pengajuan"} size="sm">
        {reviewModal.leave && (
          <div className="space-y-4">
            <div className="bg-stone-50 border border-stone-200 p-3 text-xs space-y-1">
              <div><b>{reviewModal.leave.employee_name}</b> ({reviewModal.leave.employee_code})</div>
              <div>{reviewModal.leave.category_label} · {fmtDate(reviewModal.leave.start_date)} → {fmtDate(reviewModal.leave.end_date)} · <b>{reviewModal.leave.days_count} hari</b></div>
              <div className="text-stone-600 italic">&ldquo;{reviewModal.leave.reason}&rdquo;</div>
            </div>
            <Field label={reviewModal.action === "approve" ? "Catatan (opsional)" : "Alasan Penolakan *"}>
              <textarea rows={3} value={reviewModal.note} onChange={(e) => setReviewModal({ ...reviewModal, note: e.target.value })}
                className="w-full border border-stone-300 px-3 py-2 text-sm" data-testid="cuti-review-note"
                placeholder={reviewModal.action === "approve" ? "Catatan persetujuan..." : "Jelaskan mengapa pengajuan ditolak..."} />
            </Field>
            <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
              <Button variant="outline" onClick={() => setReviewModal({ open: false, action: null, leave: null, note: "" })}>Batal</Button>
              <Button variant="primary" onClick={submitReview} disabled={reviewModal.action === "reject" && !reviewModal.note.trim()} data-testid="cuti-review-submit">
                {reviewModal.action === "approve" ? <><CheckCircle2 className="h-3.5 w-3.5" /> Setujui</> : <><XCircle className="h-3.5 w-3.5" /> Tolak</>}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
